import { InputCheckBox, SortDirection, Table, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'

import { useIncidenceTaskTypes } from 'modules/bookkeepers/hooks/TaskTypesContext'
import useIncidenceTypeQueryParam from 'modules/bookkeepers/hooks/useIncidenceTypeQueryParam'
import { useIncidenceFilterContext } from 'modules/bookkeepers/lib/IncidenceFilterContext'
import { useMessageFilterContext } from 'modules/bookkeepers/lib/MessageFilterContext'
import { useBookkeeperView } from 'modules/bookkeepers/lib/bookkeeperViewContext'
import { getIncidenceTypeNavigationMapping } from 'modules/bookkeepers/lib/incidenceTypeNavigation'
import COLUMNS from 'modules/bookkeepers/lib/tableSortColumns'

import HeaderCell from './HeaderCell'

interface Props {
  onSort: (attribute: string, direction: SortDirection) => void
  attribute: string
  direction: SortDirection
  hasIncidenceFilteredByType?: boolean
  isBookkeeperView?: boolean
  showIncidenceTypeColumn?: boolean
  showIncidencePayrollIntegrationStatus: boolean
  onToggleAll?: () => void
  totalSelectableIncidences?: number
  totalSelectedIncidences?: number
}

const filteredColumns = (
  incidenceType: string,
  showIncidenceTypeColumn: boolean,
  showIncidencePayrollIntegrationStatus: boolean
) => {
  const hasIncidenceTypeQueryParam = !!incidenceType

  return COLUMNS.filter(({ id }) => {
    if (id === 'translated_name' && !showIncidenceTypeColumn) {
      return !hasIncidenceTypeQueryParam
    } else if (id === 'translated_payroll_integration_status') {
      return showIncidencePayrollIntegrationStatus
    }

    return true
  })
}

const Header = ({
  onSort,
  attribute,
  direction,
  hasIncidenceFilteredByType = false,
  showIncidenceTypeColumn = true,
  showIncidencePayrollIntegrationStatus = false,
  onToggleAll,
  totalSelectableIncidences,
  totalSelectedIncidences,
}: Props) => {
  const taskTypes = useIncidenceTaskTypes()
  const { incidenceType } = useIncidenceTypeQueryParam()

  const tableTitles: Record<string, string> = {
    employee_first_name: i18n.t('bookkeepers.incidence.table.employee_first_name'),
    employee_last_name: i18n.t('bookkeepers.incidence.table.employee_last_name'),
    translated_name: i18n.t('bookkeepers.incidence.table.type'),
    legal_entity_name: i18n.t('bookkeepers.incidence.table.legal_entity'),
    starts_on: i18n.t('bookkeepers.incidence.table.effective_date'),
    translated_status: i18n.t('bookkeepers.incidence.table.status'),
    translated_payroll_integration_status: i18n.t(
      'bookkeepers.incidence.table.payroll_integration_status'
    ),
  }
  const isBookkeeperView = useBookkeeperView()
  const isChecked =
    totalSelectableIncidences && totalSelectedIncidences
      ? totalSelectableIncidences === totalSelectedIncidences
      : false

  const isIndeterminate =
    totalSelectableIncidences && totalSelectedIncidences
      ? totalSelectableIncidences !== totalSelectedIncidences
      : false

  const isDisabled = totalSelectableIncidences === 0

  return (
    <Table.Head>
      <Table.Row>
        {onToggleAll && (
          <Table.HeadCell width={16}>
            <InputCheckBox
              checked={isChecked}
              indeterminate={isIndeterminate}
              disabled={isDisabled}
              onClick={onToggleAll}
            />
          </Table.HeadCell>
        )}
        {filteredColumns(
          incidenceType,
          showIncidenceTypeColumn,
          showIncidencePayrollIntegrationStatus
        ).map((column) => (
          <Table.SortableHeadCell
            key={column.id}
            attribute={column.attribute}
            onSort={onSort}
            sortAttribute={attribute}
            sortDirection={direction}
          >
            <Text.Normal weight="semibold" color="grey800" size="200" whiteSpace="nowrap">
              {tableTitles[column.id]}
            </Text.Normal>
          </Table.SortableHeadCell>
        ))}

        {hasIncidenceFilteredByType &&
          taskTypes.map(({ id, translatedTitle }) => (
            <HeaderCell key={id} id={id} title={translatedTitle} />
          ))}

        {!isBookkeeperView && <Table.HeadCell />}
      </Table.Row>
    </Table.Head>
  )
}

interface AdminHeaderProps {
  messageTab: boolean
  onToggleAll: () => void
  totalSelectableIncidences: number
  totalSelectedIncidences: number
  showIncidencePayrollIntegrationStatus: boolean
}

function Admin({
  messageTab,
  onToggleAll,
  totalSelectableIncidences,
  totalSelectedIncidences,
  showIncidencePayrollIntegrationStatus,
}: AdminHeaderProps) {
  const messageFilters = useMessageFilterContext()
  const incidenceFilters = useIncidenceFilterContext()
  const { onSort, attribute, direction } = messageTab ? messageFilters : incidenceFilters

  return (
    <Header
      onSort={onSort}
      attribute={attribute}
      direction={direction}
      onToggleAll={onToggleAll}
      totalSelectableIncidences={totalSelectableIncidences}
      totalSelectedIncidences={totalSelectedIncidences}
      showIncidencePayrollIntegrationStatus={showIncidencePayrollIntegrationStatus}
    />
  )
}

interface BookkeeperHeaderProps {
  showIncidenceTypeColumn: boolean
  showIncidencePayrollIntegrationStatus: boolean
}

function Bookkeeper({
  showIncidenceTypeColumn,
  showIncidencePayrollIntegrationStatus,
}: BookkeeperHeaderProps) {
  const { onSort, attribute, direction } = useIncidenceFilterContext()
  const { incidenceType } = useIncidenceTypeQueryParam()
  const mapping = getIncidenceTypeNavigationMapping()
  const filterType = mapping[incidenceType]?.join(',')

  return (
    <Header
      onSort={onSort}
      attribute={attribute}
      direction={direction}
      hasIncidenceFilteredByType={!!filterType}
      isBookkeeperView
      showIncidenceTypeColumn={showIncidenceTypeColumn}
      showIncidencePayrollIntegrationStatus={showIncidencePayrollIntegrationStatus}
    />
  )
}

Header.Admin = Admin
Header.Bookkeeper = Bookkeeper

export default observer(Header)
